import React, { createContext, Component } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import ErrorTracker from 'error_tracker';
import ApiRequest from 'core/api/request';
import { promiser, trackEvent, BrazeUtil, getFeaturedImage, getUser } from 'core/utils';
import { getCartCount, filterEmptyCarts, checkIfProductInstanceIsInCart } from 'cart/utils';
import PersonalizeUtil from 'personalize_util';

// Max number of carts to fetch
const MAX_CARTS = 20;

const cartProviderDefaultProps = {
	activeCartId: null, // the one user is interacting with (on cart page)
	activeCartInstanceVariantMap: null,
	carts: [],
	cartCount: 0,
	loading: true,
};

const CartContext = createContext({ ...cartProviderDefaultProps });
CartContext.displayName = 'CartContext';

export class CartProvider extends Component {
	state = {
		...cartProviderDefaultProps,
		...this.props.value,
	}

	componentDidMount() {
		this.fetchCarts();

		// Listen for updates
		window.addEventListener('ShopBag.removeItem', this.handleShopBagRemoveItem);
	}

	componentDidUpdate(prevProps, prevState) {
		// Quick deep clone of carts:
		const carts = JSON.parse(JSON.stringify(this.state.carts));
		// Dispatch updates to ShopBag
		const evt = new CustomEvent('ShopBag.setCarts', {
			detail: { carts }
		});
		window.dispatchEvent(evt);
	}

	handleShopBagRemoveItem = (evt) => {
		const { detail: cartProductId } = evt;
		this.removeFromCart(cartProductId);
	}

	/**
	 * Fetches all carts regardless of cartId. (to fully populate the ShopBag on `/cart` page)
	 * Will also fetch cart by cartId if missing from list.
	 * Sets `carts` in state
	 *
	 * @param {Number} cartId - active cart id user is interacting with (on cart page)
	 * @return {Promise}
	 */
	fetchCarts = async () => {
		const [err, response] = await promiser(
			ApiRequest.get('/carts/', {
				limit: MAX_CARTS,
				page: 1,
				live_only: true,
				skip_empty: true,
			})
		);

		if (err) {
			console.log(err);
			ErrorTracker.captureException(err);
			return;
		}

		const results = response?.results || [];

		// Find (or fetch) the "active cart" and place atop list
		const activeCartId = this.state?.activeCartId;
		ErrorTracker.addBreadcrumb({
			category: 'cart',
			message: 'CartContext: fetchCarts state',
			data: JSON.parse(JSON.stringify(this.state)),
		});
		if (activeCartId) {
			let activeCart;
			let cartIx = results.findIndex(c => c.id === activeCartId);
			if (cartIx >= 0) {
				// remove from the list of carts (to place atop)
				let [foundCart] = results.splice(cartIx, 1);
				activeCart = foundCart;
			} else {
				// fetch one cart in case it was not returned by limited query above
				const [e, cart] = await promiser(ApiRequest.get(`/carts/${activeCartId}/`));
				if (cart) {
					activeCart = cart;
				}
			}
			// place active cart atop list
			if (activeCart) {
				results.unshift(activeCart);

				// fetch product instances for variant info
				this.fetchVariantInfo(activeCart);
			}
		}

		// `skip_empty` should make sure there are cart_products with quantity>0
		// `filterEmptyCarts` is a safeguard
		const carts = filterEmptyCarts(results).slice(0, MAX_CARTS);
		const count = getCartCount(carts);

		// Set cookie for ShopBag BagCounter
		Cookies.set('cartCount', count || '', { expires: 90 });

		this.setState({
			carts: [...carts],
			loading: false,
		});
	}

	/**
	 * Fetch one cart by store id
	 * In some situations it's possible `cart` is not in `CartContext`
	 * but does exist (due to limit on API calls)
	 *
	 * @param {Number} storeId - fetch cart for this store
	 * @return {Object} cart
	 */
	fetchCartByStore = async (storeId) => {
		// fetch one cart in case it was not returned by limited query above
		const [e, resp] = await promiser(ApiRequest.get(`/carts/`, {
			store_id: storeId,
			page: 1,
			limit: 1,
		}));

		if (e || !resp?.results?.length) {
			return;
		}

		// Add to state...
		let cart = resp.results[0];
		let { carts } = this.state;
		const cartIndex = carts.findIndex(c => c.id === cart.id);

		if (cartIndex >= 0) {
			// remove, to be replaced atop list
			carts.splice(cartIndex, 1);
		}

		// Add atop list
		carts.unshift(cart);

		const cartCount = getCartCount(carts);

		Cookies.set('cartCount', cartCount || '', { expires: 90 });
		this.setState({ carts, cartCount });
		return cart;
	}

	fetchVariantInfo = async (cart) => {
		if (!cart || !(cart?.products?.length > 0)) return;

		const instanceIds = cart.products.map(cp => cp.productInstanceId);
		const limit = Math.max(instanceIds.length, 10);
		const [err, response] = await promiser(
			ApiRequest.get(`/product_instances/`, {
				id__in: instanceIds.join(","),
				with_variants: true,
				page: 1,
				per_page: limit,
				limit: limit,
			})
		);

		if (err) {
			console.error(err);
			return;
		}

		const piVariantMap = {};
		response.results.forEach(pi => {
			if (pi?.variants?.length > 0) {
				piVariantMap[pi.id] = pi.variants.map(([name, val]) => val).join(", ");
			}
		});

		this.setState({
			activeCartInstanceVariantMap: Object.keys(piVariantMap).length > 0 ? piVariantMap : null,
		});
	}

	/**
	 * Return the active cart defined by `activeCartId`
	 * When no `activeCartId` is set, fallback to first cart
	 *
	 * @return {Object} activeCart
	 */
	getActiveCart = () => {
		const { activeCartId, carts } = this.state;
		if (carts.length === 0) return;
		if (!activeCartId) return carts[0];
		return carts.find(c => c.id === activeCartId);
	}

	getBrazeCartProps = (listing, product) => {
		const category = listing.categories[0] || {};
		const listingFeaturedImage = getFeaturedImage({ listingImages: listing.listingImages || listing.listing_images });
		const imageUrl = listingFeaturedImage.cdnUrl || "";

		let props = {
			listingId: listing.listingId,
			listingVersionId: listing.listingVersionId,
			quantity: 1,
			category: category.categoryName,
			slug: listing.slug,
			listingName: listing.name,
			currencyCode: listing.currencyCode,
			currencySymbol: listing.currencySymbol,
			listingBriefDescription: listing.briefDescription,
			listingMinPrice: listing.minUnitTermPrice / 100,
			listingFeaturedImage: imageUrl,
			sentAt: new Date(),
			urlParams: window.location.search,
			basicInfo: true,
		};

		if (product) {
			const productProps = {
				productName: product.productInstance.product.name,
				variant: product.productInstanceId + '_' + product.subscriptionTypeTermId,
				productInstanceId: product.productInstanceId,
				subscriptionTypeTermId: product.subscriptionTypeTermId,
				price: product.price / 100,
			};

			props = {
				...props,
				...productProps,
			};

			// Let Braze know we have product info for the mails
			props.basicInfo = false;
		}

		return props;
	}

	addProductToCart = async ({ productInstanceId, subscriptionTypeTermId, quantity, listing }) => {
		quantity = quantity || 1;

		// Actually add product to cart via API:
		const [err, cart] = await promiser(
			ApiRequest.post('/cart_products/', {
				productInstanceId,
				subscriptionTypeTermId,
				quantity,
				allowAddCartProduct: true,
			})
		);

		if (err) {
			// TODO: When product already in cart, use PUT to modify
			// NOTE: If we want to support +1 to quantity here...
			// Otherwise, just open ShopBag bc it's already there??
			console.warn(err);
			throw err;
		}

		// Add to state...
		let { carts } = this.state;
		const cartIndex = carts.findIndex(c => c.id === cart.id);

		if (cartIndex >= 0) {
			carts[cartIndex] = cart;
		} else {
			carts.unshift(cart);
		}

		const cartCount = getCartCount(carts);

		this.setState({ carts, cartCount });
		Cookies.set('cartCount', cartCount || '', { expires: 90 });

		this.firePixels({ listing, productInstanceId, cart, subscriptionTypeTermId });

		return cart;
	}

	firePixels = async ({ listing, productInstanceId, cart, subscriptionTypeTermId }) => {
		trackEvent("conversion", {
			gtag: [{ send_to: "AW-957505549/k4EPCNOrg8kBEI3AycgD" }]
		});
		trackEvent("add-to-cart", {
			gtag: [{
				'event_category': 'optimize',
				'event_label': 'add-to-cart',
				'value': 'add-to-cart',
			}]
		});

		trackEvent('PplAddToCart', {
			heap: [],
			statjoy: [],
			fullstory: [],
		});

		const product = checkIfProductInstanceIsInCart({ productInstanceId, carts: [cart], subscriptionTypeTermId });
		if (listing && getUser()) {
			const brazeProps = this.getBrazeCartProps(listing, product);
			BrazeUtil.addToCartPPL(brazeProps);
		}

		const justUnoData = {
			productid: product.id,
			name: product.productInstance.product.name,
			quantity: product.quantity,
			price: parseInt(product.price, 10) / 100,
		};

		trackEvent("cartItemAdd", {
			justuno: [justUnoData]
		});

		if(listing) {
			PersonalizeUtil.onAddToCart(listing.id);
		}
	}

	addCouponToCart = async (cartId, couponCode) => {
		if (!cartId || !couponCode) return;

		let { carts } = this.state;
		const cartIndex = carts.findIndex(c => c.id === cartId);

		if (cartIndex < 0) return;

		// If we have cart coupon, then delete those first
		// Checkout currently only supports one coupon per order
		if (carts[cartIndex]?.coupons?.length > 0) {
			const allResp = await Promise.all(
				carts[cartIndex].coupons.map(cartCoupon => ApiRequest.delete(`/carts/${cartId}/coupons/${cartCoupon.id}/`))
			);
		}

		// Add coupon to cart via API:
		const [err, cart] = await promiser(
			ApiRequest.post(`/carts/${cartId}/coupons/`, {
				cartId,
				couponCode,
			})
		);

		if (err) {
			console.warn(err);
			throw err;
		}

		// Update cart in state
		carts[cartIndex] = cart;
		this.setState({ carts });

		return cart;
	}

	removeFromCart = async (cartProductId) => {
		// Actually delete
		await ApiRequest.delete(`/cart_products/${cartProductId}/`);

		// And remove from state...
		let removedCartProduct;
		let { carts } = this.state;
		for (let i = 0; i < carts.length; i++) {
			let cpIndex = carts[i].products.findIndex(cp => cp.id === cartProductId);
			if (cpIndex >= 0) {
				removedCartProduct = carts[i].products[cpIndex];
				// remove from its cart
				carts[i].products = carts[i].products.filter(cp => cp.id !== cartProductId);
				break;
			}
		}

		carts = filterEmptyCarts(carts);

		const cartCount = getCartCount(carts);

		this.setState({ carts, cartCount });
		Cookies.set('cartCount', cartCount || '', { expires: 90 });

		// Pixels etc
		trackEvent("remove_from_cart", {
			braze: [{
				listingId: removedCartProduct.productInstance.product.id,
				variant: removedCartProduct.subscriptionTypeTerm?.id ? removedCartProduct.productInstance.id + '_' + removedCartProduct.subscriptionTypeTerm.id : removedCartProduct.productInstance.id,
				productInstanceId: removedCartProduct.productInstance.id,
				subscriptionTypeTermId: removedCartProduct.subscriptionTypeTerm?.id,
				quantity: 1,
			}],
		});
	}

	render() {
		const {
			state,
			addProductToCart,
			addCouponToCart,
			fetchCartByStore,
			removeFromCart,
			getActiveCart,
		} = this;
		const { children } = this.props;
		const value = {
			...state,
			addProductToCart,
			addCouponToCart,
			fetchCartByStore,
			removeFromCart,
			getActiveCart,
		};

		return (
			<CartContext.Provider value={value}>
				{children}
			</CartContext.Provider>
		);
	}
}

CartProvider.sharedPropTypes = {
	activeCartId: PropTypes.number,
	activeCartInstanceVariantMap: PropTypes.object,
	carts: PropTypes.arrayOf(PropTypes.object),
	cartCount: PropTypes.number,
	loading: PropTypes.bool,
};

CartProvider.propTypes = {
	children: PropTypes.node.isRequired,
	value: PropTypes.shape({
		...CartProvider.sharedPropTypes,
	}),
};

CartProvider.defaultProps = {
	value: {
		...cartProviderDefaultProps,
	},
};

/*
* Provide children down the tree with access to this data
*/
export const CartConsumer = CartContext.Consumer;

export default CartContext;
